import { Modal, notification, Spin, Upload } from "antd";
import cuid from "cuid";
import { isEmpty, isNil, noop } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlinePictureAsPdf, MdOutlineUploadFile } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { DOCUMENT_TEMPLATE_STATUSES, SCREENS } from "utils/constants";
import { autoMappings, zonesOf } from "../field-status";
import { PDF_ACCEPT, PDF_PHASES, PdfRetry, usePdfAnchors } from "../pdf-upload";

const PDF_EXTENSION = /\.pdf$/i;

const NewDocumentModal = ({ onClose }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS);
  const NEW_DOCUMENT = t("NEW_DOCUMENT", { returnObjects: true });
  const navigate = useNavigate();
  const id = useMemo(() => cuid(), []);
  const [name, setName] = useState("");
  const { phase, file, anchors, beforeUpload, attempt } = usePdfAnchors({
    onReady: (picked) => setName(picked.name.replace(PDF_EXTENSION, "")),
  });

  const [addDocumentTemplate, { loading: creating }] = useMutation(
    graphql.mutations.CREATE_DOCUMENT_TEMPLATE,
    { onError: noop },
  );

  const zoneCount = zonesOf(anchors).length;
  const canCreate =
    phase === PDF_PHASES.READY && !isEmpty(name.trim()) && !creating;

  const create = async () => {
    if (!canCreate) return;
    const started = attempt();
    const { data } = await addDocumentTemplate({
      variables: {
        data: {
          id,
          name: name.trim(),
          anchors,
          anchorMappings: autoMappings(anchors),
          status: DOCUMENT_TEMPLATE_STATUSES.DRAFT,
          file,
        },
      },
    });
    if (attempt() !== started) return;
    const created = data?.addDocumentTemplate;
    if (isNil(created?.id)) {
      notification.error({ message: NEW_DOCUMENT.CREATE_FAILED });
      return;
    }
    navigate(
      `${SCREENS.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS.EDIT.path}/${created.id}`,
    );
  };

  const failure = (message) => (
    <PdfRetry
      className="dynamic-documents__new-failure"
      message={message}
      label={NEW_DOCUMENT.OTHER_FILE}
      onUpload={beforeUpload}
    />
  );

  const bodies = {
    [PDF_PHASES.PICK]: (
      <Upload.Dragger
        accept={PDF_ACCEPT}
        beforeUpload={beforeUpload}
        showUploadList={false}
        maxCount={1}
        className="dynamic-documents__drop"
      >
        <MdOutlineUploadFile
          size={32}
          className="dynamic-documents__drop-icon"
        />
        <p className="dynamic-documents__drop-text">{NEW_DOCUMENT.DROP}</p>
      </Upload.Dragger>
    ),
    [PDF_PHASES.READING]: (
      <div className="dynamic-documents__new-reading">
        <Spin />
        <span>{NEW_DOCUMENT.READING}</span>
      </div>
    ),
    [PDF_PHASES.READ_FAILED]: failure(NEW_DOCUMENT.READ_FAILED),
    [PDF_PHASES.NO_ZONE]: failure(NEW_DOCUMENT.NO_ZONE),
    [PDF_PHASES.READY]: (
      <div className="dynamic-documents__new-ready">
        <div className="dynamic-documents__new-file">
          <MdOutlinePictureAsPdf size={20} />
          <span className="dynamic-documents__new-file-name">{file?.name}</span>
        </div>
        <p className="dynamic-documents__new-zones">
          {t("NEW_DOCUMENT.ZONES", { count: zoneCount })}
        </p>
        <Input
          label={NEW_DOCUMENT.NAME}
          value={name}
          onChange={({ target: { value } }) => setName(value)}
          onPressEnter={create}
          autoFocus
        />
      </div>
    ),
  };

  return (
    <Modal
      open
      centered
      title={NEW_DOCUMENT.TITLE}
      onCancel={onClose}
      closable={!creating}
      maskClosable={!creating}
      keyboard={!creating}
      footer={
        <div className="dynamic-documents__new-footer">
          <Button type="gray--link" onClick={onClose} disabled={creating}>
            {NEW_DOCUMENT.CANCEL}
          </Button>
          <Button
            type="primary"
            onClick={create}
            loading={creating}
            disabled={!canCreate}
          >
            {NEW_DOCUMENT.CREATE}
          </Button>
        </div>
      }
    >
      <div className="dynamic-documents__new">{bodies[phase]}</div>
    </Modal>
  );
};

export default NewDocumentModal;
